import { contractService, ContractStatus } from "@services/contractService";
import { useAuthStore } from "@store/authStore";
import { cn } from "@utils";
import { useCallback, useEffect, useState } from "react";
import Button from "./Button";
import Spinner from "./Spinner";

interface ContractModalProps {
  isOpen: boolean;
  onContractSigned: () => void;
}

const CONTRACT_TITLES: Record<string, string> = {
  individual: "Individual Class Contract",
  duo: "Duo Class Contract",
  group: "Group Class Contract",
};

const CONTRACT_SUBTITLES: Record<string, string> = {
  individual: "Terms and conditions for personalized private lessons",
  duo: "Terms and conditions for shared classes with a partner",
  group: "Terms and conditions for group classes",
};

export function ContractModal({
  isOpen,
  onContractSigned,
}: ContractModalProps) {
  const user = useAuthStore((state) => state.user);

  const [contractStatus, setContractStatus] = useState<ContractStatus | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fullName, setFullName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const fetchContractStatus = useCallback(async () => {
    try {
      setLoading(true);
      const status = await contractService.getContractStatus();
      setContractStatus(status);
    } catch (err) {
      setError("Failed to load contract status");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchContractStatus();
    }
  }, [isOpen, fetchContractStatus]);

  const handleScroll = (e: React.UIEvent<HTMLIFrameElement>) => {
    const iframe = e.currentTarget;
    if (iframe) {
      setHasScrolledToBottom(true);
    }
  };

  const handleSign = async () => {
    if (!acceptedTerms || !fullName.trim()) {
      setError("Please accept the terms and type your full name to sign.");
      return;
    }

    try {
      setSigning(true);
      setError(null);
      await contractService.signContract({
        fullName: fullName.trim(),
        acceptedTerms,
      });
      onContractSigned();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign contract";
      if (typeof err === "object" && err !== null && "response" in err) {
        const response = (
          err as {
            response?: {
              data?: {
                full_name?: string[];
                accepted_terms?: string[];
                non_field_errors?: string[];
              };
            };
          }
        ).response;
        if (response?.data) {
          const data = response.data;
          if (data.full_name) {
            setError(data.full_name[0]);
          } else if (data.accepted_terms) {
            setError(data.accepted_terms[0]);
          } else if (data.non_field_errors) {
            setError(data.non_field_errors[0]);
          } else {
            setError(errorMessage);
          }
        } else {
          setError(errorMessage);
        }
      } else {
        setError(errorMessage);
      }
    } finally {
      setSigning(false);
    }
  };

  if (!isOpen) return null;

  const contractType = contractStatus?.contractType;
  const pdfUrl = contractType
    ? contractService.getContractPdfUrl(
        contractType,
        contractStatus?.contractFileId,
      )
    : null;

  // Determine current step based on state
  const currentStep = !hasScrolledToBottom
    ? 1
    : !acceptedTerms
      ? 2
      : !fullName.trim()
        ? 3
        : 4;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-earth-600 text-white px-6 py-4 flex items-center justify-between gap-4 shrink-0">
          <div>
            <h2 className="text-lg font-semibold leading-tight">
              {contractType
                ? CONTRACT_TITLES[contractType]
                : "Service Contract"}
            </h2>
            <p className="text-earth-100 text-sm mt-1 max-w-xl">
              {contractType
                ? CONTRACT_SUBTITLES[contractType]
                : "Please read and sign the contract to access the platform"}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {contractType && (
              <span className="text-xs bg-earth-500/30 px-2 py-1 rounded-full">
                {contractType.toUpperCase()}
              </span>
            )}
          </div>
        </div>

        {/* Content with scroll */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Spinner size="lg" />
                <span className="ml-3 text-gray-600">Loading contract...</span>
              </div>
            ) : contractStatus && !contractStatus.needsSignature ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-10 h-10 text-success-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Contract Already Signed
                  </h3>
                  <p className="text-gray-600">{contractStatus.reason}</p>
                </div>
              </div>
            ) : (
              <>
                {/* Steps Instructions */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">
                    How to sign your contract
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Step 1 */}
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
                          currentStep === 1
                            ? "bg-olive-600 text-white"
                            : currentStep > 1
                              ? "bg-success-500 text-white"
                              : "bg-gray-200 text-gray-600",
                        )}
                      >
                        {currentStep > 1 ? "✓" : "1"}
                      </div>
                      <div>
                        <p
                          className={cn(
                            "font-medium text-sm",
                            currentStep >= 1
                              ? "text-gray-900"
                              : "text-gray-500",
                          )}
                        >
                          Read contract
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Review the full document
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
                          currentStep === 2
                            ? "bg-earth-600 text-white"
                            : currentStep > 2
                              ? "bg-success-500 text-white"
                              : "bg-gray-200 text-gray-600",
                        )}
                      >
                        {currentStep > 2 ? "✓" : "2"}
                      </div>
                      <div>
                        <p
                          className={cn(
                            "font-medium text-sm",
                            currentStep >= 2
                              ? "text-gray-900"
                              : "text-gray-500",
                          )}
                        >
                          Accept terms
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Check the agreement box
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
                          currentStep === 3
                            ? "bg-earth-600 text-white"
                            : currentStep > 3
                              ? "bg-success-500 text-white"
                              : "bg-gray-200 text-gray-600",
                        )}
                      >
                        {currentStep > 3 ? "✓" : "3"}
                      </div>
                      <div>
                        <p
                          className={cn(
                            "font-medium text-sm",
                            currentStep >= 3
                              ? "text-gray-900"
                              : "text-gray-500",
                          )}
                        >
                          Type your name
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Sign with full name
                        </p>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          "w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold shrink-0",
                          currentStep === 4
                            ? "bg-earth-600 text-white"
                            : "bg-gray-200 text-gray-600",
                        )}
                      >
                        4
                      </div>
                      <div>
                        <p
                          className={cn(
                            "font-medium text-sm",
                            currentStep === 4
                              ? "text-gray-900"
                              : "text-gray-500",
                          )}
                        >
                          Confirm
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Click "Sign Contract"
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* PDF Viewer */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">
                      Contract Document
                    </h4>
                    {pdfUrl && (
                      <a
                        href={pdfUrl.replace("/preview", "/view")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-earth-600 hover:text-earth-700 flex items-center gap-1.5"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                        Open in new tab
                      </a>
                    )}
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                    {pdfUrl ? (
                      <iframe
                        src={pdfUrl}
                        className="w-full h-[550px]"
                        title="Contract PDF"
                        allow="autoplay"
                        onLoad={() => setHasScrolledToBottom(true)}
                        onScroll={handleScroll}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-[550px] text-gray-500">
                        <div className="text-center">
                          <svg
                            className="w-12 h-12 mx-auto mb-3 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <p>Contract not available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Terms Acceptance Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
                  <h4 className="font-medium text-gray-900">
                    Digital Signature
                  </h4>

                  {error && (
                    <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                      <svg
                        className="w-5 h-5 shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {error}
                    </div>
                  )}

                  {/* Checkbox */}
                  <div
                    className={cn(
                      "flex items-start gap-3 p-4 rounded-lg border transition-all",
                      acceptedTerms
                        ? "bg-success-50 border-success-200"
                        : hasScrolledToBottom
                          ? "bg-gray-50 border-gray-300 hover:border-earth-300"
                          : "bg-gray-50 border-gray-200",
                    )}
                  >
                    <input
                      type="checkbox"
                      id="accept-terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      disabled={!hasScrolledToBottom || signing}
                      className={cn(
                        "mt-0.5 h-4 w-4 rounded border-gray-300 text-earth-600 focus:ring-earth-500",
                        !hasScrolledToBottom && "opacity-50 cursor-not-allowed",
                      )}
                    />
                    <label
                      htmlFor="accept-terms"
                      className="text-sm text-gray-700"
                    >
                      I have read, understood, and agree to all the terms and
                      conditions of this contract. I understand that this is a
                      legally binding agreement.
                    </label>
                  </div>

                  {/* Name Input */}
                  <div className="space-y-2">
                    <label
                      htmlFor="full-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Type your full name to sign:
                    </label>
                    <input
                      type="text"
                      id="full-name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder={user?.fullName || "Your full name"}
                      disabled={!acceptedTerms || signing}
                      className={cn(
                        "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-earth-500 focus:border-earth-500 transition-all",
                        "font-serif text-lg italic",
                        !acceptedTerms
                          ? "opacity-50 cursor-not-allowed bg-gray-50 border-gray-200"
                          : "border-gray-300 bg-white hover:border-gray-400",
                      )}
                    />
                    <p className="text-xs text-gray-500">
                      Your name must match exactly:{" "}
                      <span className="font-medium text-gray-700">
                        {user?.fullName}
                      </span>
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        {contractStatus?.needsSignature && (
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between shrink-0">
            <p className="text-sm text-gray-500">
              By signing, you agree to the contract terms
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSign}
              disabled={!acceptedTerms || !fullName.trim() || signing}
              isLoading={signing}
              className="bg-earth-600 hover:bg-earth-700 px-8 text-white"
            >
              {signing ? "Signing..." : "Sign Contract"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
